import React, { useState, useMemo } from 'react';
import { Spin, Input, Tag, Empty, Tooltip } from 'antd';
import { SearchOutlined, LoadingOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../../../api/client';

/**
 * SrefStylesTab - Displays Midjourney sref examples in the media drawer
 * Shows style thumbnails with sref codes that can be copied
 */
const SrefStylesTab = ({ onMediaClick, mediaViewMode = 'grid' }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedCode, setCopiedCode] = useState(null);
    const [expandedSref, setExpandedSref] = useState(null);

    // Fetch srefs from API
    const { data: srefs, isLoading, error } = useQuery({
        queryKey: ['midjourney-srefs'],
        queryFn: async () => {
            const response = await api.get('/midjourney/srefs');
            // API returns { success, count, data }
            return response.data?.data || [];
        },
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    // Filter srefs based on search
    const filteredSrefs = useMemo(() => {
        if (!srefs) return [];
        if (!searchTerm.trim()) return srefs;

        const term = searchTerm.toLowerCase();
        return srefs.filter(sref =>
            sref.styleName?.toLowerCase().includes(term) ||
            sref.srefCode?.includes(term) ||
            sref.styleTags?.some(tag => tag.toLowerCase().includes(term))
        );
    }, [srefs, searchTerm]);

    // Copy sref code to clipboard
    const handleCopySref = async (e, srefCode) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(`--sref ${srefCode}`);
            setCopiedCode(srefCode);
            setTimeout(() => setCopiedCode(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Toggle expanded view for a sref
    const handleSrefClick = (sref) => {
        if (expandedSref?.id === sref.id) {
            setExpandedSref(null);
        } else {
            setExpandedSref(sref);
        }
    };

    // Handle image click for preview
    const handleImageClick = (e, sref, imageIndex) => {
        e.stopPropagation();
        if (onMediaClick && sref.examples) {
            const mediaItems = sref.examples.map((ex, idx) => ({
                id: `${sref.srefCode}-${idx}`,
                src: ex.url,
                type: 'image',
                label: `${sref.styleName} - Example ${idx + 1}`,
                srefCode: sref.srefCode,
                styleVersion: ex.styleVersion,  // Per-image styleVersion
                fullPrompt: ex.fullPrompt       // Per-image fullPrompt
            }));
            onMediaClick(imageIndex, mediaItems);
        }
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ color: '#8b949e', textAlign: 'center', padding: 40 }}>
                Failed to load sref styles. Please try again.
            </div>
        );
    }

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Search */}
            <div style={{ padding: '20px 20px 0 20px', flexShrink: 0 }}>
                <Input
                    prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
                    placeholder="Search styles, tags, or codes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        background: '#21262d',
                        border: '1px solid #30363d',
                        color: '#fff',
                        borderRadius: 6,
                        marginBottom: 16
                    }}
                />
            </div>

            {/* Sref Grid */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 20px 20px' }}>
                {filteredSrefs.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <span style={{ color: '#8b949e' }}>
                                {searchTerm ? 'No matching styles found' : 'No sref styles yet. Run the scraper to populate!'}
                            </span>
                        }
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {filteredSrefs.map((sref) => (
                            <div
                                key={sref.id}
                                onClick={() => handleSrefClick(sref)}
                                style={{
                                    background: '#161b22',
                                    border: expandedSref?.id === sref.id ? '1px solid #1890ff' : '1px solid #30363d',
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'border-color 0.2s, transform 0.2s',
                                }}
                            >
                                {/* Header */}
                                <div style={{
                                    padding: '12px 16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    background: '#21262d'
                                }}>
                                    <div>
                                        <div style={{
                                            color: '#c9d1d9',
                                            fontWeight: 600,
                                            fontSize: 14,
                                            marginBottom: 4
                                        }}>
                                            {sref.styleName || 'Untitled Style'}
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {sref.styleVersion && (
                                                <Tag color="magenta" style={{ margin: 0, fontSize: 10 }}>
                                                    --sv {sref.styleVersion}
                                                </Tag>
                                            )}
                                            {sref.styleTags?.slice(0, 3).map((tag, idx) => (
                                                <Tag key={idx} style={{ margin: 0, fontSize: 10, background: '#30363d', border: 'none', color: '#8b949e' }}>
                                                    {tag}
                                                </Tag>
                                            ))}
                                            {sref.styleTags?.length > 3 && (
                                                <span style={{ color: '#8b949e', fontSize: 10 }}>
                                                    +{sref.styleTags.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Sref Code Copy Button */}
                                    <Tooltip title={copiedCode === sref.srefCode ? 'Copied!' : 'Copy --sref code'}>
                                        <div
                                            onClick={(e) => handleCopySref(e, sref.srefCode)}
                                            style={{
                                                background: copiedCode === sref.srefCode ? 'rgba(82, 196, 26, 0.2)' : '#30363d',
                                                padding: '6px 10px',
                                                borderRadius: 6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                cursor: 'pointer',
                                                transition: 'background 0.2s'
                                            }}
                                        >
                                            {copiedCode === sref.srefCode ? (
                                                <CheckOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                                            ) : (
                                                <CopyOutlined style={{ color: '#8b949e', fontSize: 12 }} />
                                            )}
                                            <span style={{
                                                color: copiedCode === sref.srefCode ? '#52c41a' : '#c9d1d9',
                                                fontSize: 12,
                                                fontFamily: 'monospace'
                                            }}>
                                                {sref.srefCode}
                                            </span>
                                        </div>
                                    </Tooltip>
                                </div>

                                {/* Image Preview Row */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: expandedSref?.id === sref.id
                                        ? 'repeat(auto-fill, minmax(100px, 1fr))'
                                        : 'repeat(4, 1fr)',
                                    gap: 2,
                                    padding: 2
                                }}>
                                    {(expandedSref?.id === sref.id ? sref.examples : sref.examples?.slice(0, 4))?.map((example, idx) => (
                                        <div
                                            key={idx}
                                            onClick={(e) => handleImageClick(e, sref, idx)}
                                            style={{
                                                aspectRatio: '1/1',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                position: 'relative'
                                            }}
                                        >
                                            <img
                                                src={example.url}
                                                alt={`${sref.styleName} example ${idx + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    transition: 'transform 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                            />
                                            {!expandedSref && idx === 3 && sref.examples?.length > 4 && (
                                                <div style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    background: 'rgba(0,0,0,0.7)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                    fontSize: 16
                                                }}>
                                                    +{sref.examples.length - 4}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Stats Footer */}
            <div style={{
                padding: '12px 20px',
                borderTop: '1px solid #30363d',
                background: '#161b22',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span style={{ color: '#8b949e', fontSize: 11 }}>
                    {filteredSrefs.length} style{filteredSrefs.length !== 1 ? 's' : ''}
                </span>
                <a
                    href="https://sref-midjourney.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1890ff', fontSize: 11 }}
                >
                    sref-midjourney.com ↗
                </a>
            </div>
        </div>
    );
};

export default SrefStylesTab;
